import { useState } from 'react'
import { parseAsArrayOf, parseAsString, useQueryStates } from 'nuqs'
import { useDebounce } from '@/hooks/useDebounce'

// Define all filter parsers - align with useInvoiceQuery expected parameters
const containerFilterParsers = {
  // Status filters
  status: parseAsArrayOf(parseAsString, ',').withDefault(['all']).withOptions({
    clearOnDefault: true,
  }),

  // Category filters
  category: parseAsArrayOf(parseAsString, ',')
    .withDefault(['all'])
    .withOptions({
      clearOnDefault: true,
    }),

  // Returned filter
  isReturned: parseAsString.withDefault('all').withOptions({
    clearOnDefault: true,
  }),

  // Company filters
  ownerCompanyId: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),
  logisticCompanyId: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),
  transporterCompanyId: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),
  storageCompanyId: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),
}

export function useContainerFilters() {
  // Search usa useState + debounce (igual que inventory)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  const [filters, setFilters] = useQueryStates(containerFilterParsers, {
    history: 'replace', // Use replace to avoid cluttering browser history
    shallow: true, // Client-side only updates
  })

  // Helper functions for easier state management
  const updateSearch = (search: string) => {
    setSearchQuery(search)
  }

  const updateStatus = (statuses: string[]) => {
    setFilters((prev) => ({ ...prev, status: statuses }))
  }

  const updateCategory = (categories: string[]) => {
    setFilters((prev) => ({ ...prev, category: categories }))
  }

  const updateIsReturned = (isReturned: string) => {
    setFilters((prev) => ({ ...prev, isReturned }))
  }

  const updateOwnerCompanyId = (ownerCompanyId: string) => {
    setFilters((prev) => ({ ...prev, ownerCompanyId }))
  }

  const updateLogisticCompanyId = (logisticCompanyId: string) => {
    setFilters((prev) => ({ ...prev, logisticCompanyId }))
  }

  const updateTransporterCompanyId = (transporterCompanyId: string) => {
    setFilters((prev) => ({ ...prev, transporterCompanyId }))
  }

  const updateStorageCompanyId = (storageCompanyId: string) => {
    setFilters((prev) => ({ ...prev, storageCompanyId }))
  }

  const clearAllFilters = () => {
    // Clear all filters in a single atomic operation to prevent step-by-step clearing
    setSearchQuery('')
    setFilters({
      status: ['all'],
      category: ['all'],
      isReturned: 'all',
      ownerCompanyId: '',
      logisticCompanyId: '',
      transporterCompanyId: '',
      storageCompanyId: '',
    })
  }

  // Check if any filters are active (excluding defaults)
  const hasActiveFilters =
    debouncedSearchQuery !== '' ||
    !filters.status.includes('all') ||
    !filters.category.includes('all') ||
    filters.isReturned !== 'all' ||
    (filters.ownerCompanyId !== '' && filters.ownerCompanyId !== 'all') ||
    (filters.logisticCompanyId !== '' && filters.logisticCompanyId !== 'all') ||
    (filters.transporterCompanyId !== '' &&
      filters.transporterCompanyId !== 'all') ||
    (filters.storageCompanyId !== '' && filters.storageCompanyId !== 'all')

  const finalFilters = {
    ...filters,
    search: debouncedSearchQuery, // Usar el valor debounced
  }
  return {
    // Current filter values - ahora con search separado
    filters: finalFilters,
    // Valores sin debounce para el input
    searchQuery,
    debouncedSearchQuery,

    // Update functions
    updateSearch,
    updateStatus,
    updateCategory,
    updateIsReturned,
    updateOwnerCompanyId,
    updateLogisticCompanyId,
    updateTransporterCompanyId,
    updateStorageCompanyId,
    setFilters,
    clearAllFilters,

    // Helper states
    hasActiveFilters,
  }
}

// Type exports for better TypeScript support
export type ContainerFilters = ReturnType<typeof useContainerFilters>['filters']
export type ContainerFilterKey = keyof typeof containerFilterParsers
